import { useEffect, useRef, useState } from "react";
import { MoreVertical, Eye, Pencil, ShieldCheck, Mail, KeyRound, Lock, UserX, UserCheck, Trash2, Crown, XCircle } from "lucide-react";

const Item = ({ icon: Icon, label, onClick, danger, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-semibold transition-colors duration-150 ${
      danger
        ? "text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
        : "text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/10"
    }`}
  >
    <Icon size={15} /> {label}
  </button>
);

export default function OrgUserActionsMenu({ row, onAction, disabled }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [open]);

  const toggleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuHeight = 320;
      const openUpward = window.innerHeight - rect.bottom < menuHeight && rect.top > menuHeight;
      setCoords({
        top: openUpward ? rect.top - menuHeight - 4 : rect.bottom + 4,
        left: rect.right - 224,
      });
    }
    setOpen((v) => !v);
  };

  const act = (type) => { setOpen(false); onAction(type, row); };

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        onClick={toggleOpen}
        disabled={disabled}
        data-testid={`org-user-actions-btn-${row.id}`}
        className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 dark:hover:bg-white/10 dark:hover:text-white"
      >
        <MoreVertical size={17} />
      </button>
      {open && (
        <div
          ref={menuRef}
          data-testid={`org-user-actions-menu-${row.id}`}
          style={{ position: "fixed", top: coords.top, left: coords.left }}
          className="z-[60] w-56 overflow-hidden rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl dark:border-white/10 dark:bg-[#111C33]"
        >
          {row.kind === "invite" ? (
            <>
              <Item icon={Mail} label="Resend Invitation" onClick={() => act("resend_invite")} testId={`org-user-action-resend-${row.id}`} />
              <Item icon={XCircle} label="Cancel Invitation" onClick={() => act("cancel_invite")} danger testId={`org-user-action-cancel-invite-${row.id}`} />
            </>
          ) : (
            <>
              <Item icon={Eye} label="View Profile" onClick={() => act("view")} testId={`org-user-action-view-${row.id}`} />
              <Item icon={Pencil} label="Edit User" onClick={() => act("edit")} testId={`org-user-action-edit-${row.id}`} />
              {row.isOwner ? (
                <Item icon={Crown} label="Transfer Ownership" onClick={() => act("transfer")} testId={`org-user-action-transfer-${row.id}`} />
              ) : (
                <Item icon={ShieldCheck} label="Change Roles" onClick={() => act("role")} testId={`org-user-action-role-${row.id}`} />
              )}
              {row.passwordEligible && (
                <Item icon={KeyRound} label="Reset Password" onClick={() => act("reset_password")} testId={`org-user-action-reset-${row.id}`} />
              )}
              {!row.isOwner && <Item icon={Lock} label="Manage Access" onClick={() => act("access")} testId={`org-user-action-access-${row.id}`} />}
              {!row.isOwner && (
                row.status === "Deactivated" ? (
                  <Item icon={UserCheck} label="Reactivate User" onClick={() => act("reactivate")} testId={`org-user-action-reactivate-${row.id}`} />
                ) : (
                  <Item icon={UserX} label="Deactivate User" onClick={() => act("deactivate")} testId={`org-user-action-deactivate-${row.id}`} />
                )
              )}
              {!row.isOwner && <Item icon={Trash2} label="Remove User" onClick={() => act("remove")} danger testId={`org-user-action-remove-${row.id}`} />}
            </>
          )}
        </div>
      )}
    </>
  );
}
